import type { PropsWithChildren, ReactNode } from "react";
import { cn } from "./cn";
import { Grid3DBackdrop } from "./Grid3DBackdrop";
import { MobileBottomNav } from "./MobileBottomNav";

export interface AppShellProps extends PropsWithChildren {
  topBar: ReactNode;
  /**
   * "narrative" (default) — page scrolls vertically across sections.
   * "fullscreen" — legacy fixed viewport shell (used by #auth / grid / canvas
   *   routes that own their internal layout, so `main` clips overflow).
   * "scroll" — fixed viewport shell whose `main` scrolls vertically. Used by
   *   document-style routes (Profile / Dashboard / Frozen Artwork) whose
   *   content is taller than the viewport.
   */
  variant?: "narrative" | "fullscreen" | "scroll";
  className?: string;
}

/**
 * AppShell — WO#5 Neon-Premium pass (per Claudio's authoritative
 * visual reference).
 *
 * Owns the fixed atmospheric backdrop (perspective floor + digital
 * city silhouette + horizon halo + noise) so the neon world stays
 * behind everything as the user scrolls. Sections above are treated
 * as translucent glass surfaces layered on top.
 */
export function AppShell({
  topBar,
  children,
  variant = "narrative",
  className,
}: AppShellProps) {
  if (variant === "fullscreen" || variant === "scroll") {
    return (
      <div
        data-testid="app-shell"
        data-shell-variant={variant}
        className={cn(
          "relative flex h-screen w-screen flex-col overflow-hidden",
          "bg-obsidian text-text-primary font-sans antialiased",
          className,
        )}
      >
        <Grid3DBackdrop variant="fullscreen" />
        <div className="relative z-topbar shrink-0">{topBar}</div>
        <main
          data-testid="app-workspace"
          className={cn(
            "relative z-canvas flex-1 min-h-0 min-w-0",
            variant === "scroll"
              ? "overflow-y-auto overflow-x-hidden pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] md:pb-0"
              : "overflow-hidden",
          )}
        >
          {children}
        </main>
        {/* Macro #31 — mobile bottom nav on document-style surfaces only
            (never the fullscreen editors that own their own chrome). */}
        {variant === "scroll" && <MobileBottomNav />}
      </div>
    );
  }

  return (
    <div
      data-testid="app-shell"
      data-shell-variant="narrative"
      className={cn(
        "relative min-h-screen w-full",
        "bg-obsidian text-text-primary font-sans antialiased",
        "overflow-x-clip",
        className,
      )}
    >
      <Grid3DBackdrop variant="narrative" />
      <div className="top-nav-wrap sticky top-0 z-topbar">{topBar}</div>
      <main
        data-testid="app-workspace"
        className="relative z-canvas pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] md:pb-0"
      >
        {children}
      </main>
      <MobileBottomNav />
    </div>
  );
}
